import {debounce, throttle} from 'throttle-debounce';
import CustomSelect from '@/components/form/custom-select';
import ParametricFilterContentPopup from './content-popup';
import ParametricFilterPopup from './popup';
import RangeSlider from '@/components/range-slider-double';

class ParametricFilter {
    constructor(options) {
        /**
         * Параметры
         */
        this.options = options;

        /**
         * Целевой элемент
         */
        this.element = options.element ? options.element.querySelector('.j-parametric-filter') : null;

        /**
         * CSS-класс состояния загрузки
         * @type {string}
         */
        this.loadingClass = 'is-loading';

        /**
         * CSS-класс неактивного состояния
         * @type {string}
         */
        this.disableClass = 'is-disabled';

        /**
         * CSS-класс активного состояния
         * @type {string}
         */
        this.activeClass = 'is-active';

        /**
         * CSS-класс процесса
         * @type {string}
         */
        this.processClass = 'is-process';

        /**
         * CSS-класс фиксированного поиска в области скролла
         * @type {string}
         */
        this.scrollClass = 'is-scroll';

        /**
         * Класс для элемента фильтра в состоянии загрузки
         * @type {string}
         */
        this.elemLoadingClass = 'is-filter-elem-load';

        /**
         * Класс для элемента фильтра в состоянии checked
         * @type {string}
         */
        this.checkedClass = 'is-checked';

        /**
         * Задержка отложенных функций
         * @type {number}
         */
        this.debTime = 300;

        /**
         * Тип последнего запроса
         * @type {string}
         */
        this.reqType = '';

        this.sliderTitles = null;
    }

    init() {
        if (!this.element) {
            return;
        }

        this._initElements();
        this._initSelects();
        this._initContentPopups();
        this._initSliders();
        this._initPopup();

        this._bindEvents();
        this._roomsEvents();
    }

    /**
     * Инит элементов
     * @private
     */
    _initElements() {
        this.form = this.element.querySelector('.j-parametric-filter__form');
        this.reset = this.element.querySelector('.j-parametric-filter__reset');
        this.submit = this.element.querySelector('.j-parametric-filter__submit');
        this.submitText = this.element.querySelector('.j-parametric-filter__submit-text');
        this.filterElems = Array.from(this.element.querySelectorAll('.j-parametric-filter__elem'));
        this.visualParamInputs = Array.from(this.element.querySelectorAll('.j-visual-param input'));

        // Враппер со списком квартир
        this.wrapper = document.querySelector('.j-parametric');
        this.flatsList = this.wrapper?.querySelector('.j-parametric-result__content');
        // Кнопки смены вида
        this.viewButtons = Array.from(this.wrapper?.querySelectorAll('.j-parametric-result__view'));

        // Чекбоксы комностности
        this.rooms = Array.from(this.element.querySelectorAll('.j-checkbox-parametric .checkbox__input'));
        this.sliderTitles = Array.from(this.element.querySelectorAll('.j-range-slider__input-value'));
    }

    /**
     * Инициализация селектов
     * @private
     */
    _initSelects() {
        this.selectElements = Array.from(this.element.querySelectorAll('.j-parametric-filter__select'));
        this.selects = [];
        this.selectElements.forEach((element) => {
            const select = new CustomSelect({
                elem: element
            });

            select.init();
            this.selects.push(select);
        });
    }

    /**
     * Инициализация псевдоселектов с попапами
     * @private
     */
    _initContentPopups() {
        this.selectPopupElements = Array.from(this.element.querySelectorAll('.j-parametric-select-popup'));
        this.selectPopups = [];
        this.selectPopupElements.forEach((element) => {
            const select = new ParametricFilterContentPopup({
                elem: element
            });

            select.init();
            this.selectPopups.push(select);
        });
    }

    /**
     * Инициализация рейндж-слайдеров
     * @private
     */
    _initSliders() {
        this.rangeElements = Array.from(this.element.querySelectorAll('.j-range-slider'));
        this.ranges = {};
        this.rangeElements.forEach((element) => {
            const range = new RangeSlider();

            range.init({
                wrapper : element,
                onFinish: debounce(this.debTime, this._send.bind(this, 'filter'))
            });

            this.ranges[element.dataset.slider] = range;
        });

        this._updateSliderTitles();
    }

    /**
     * Инит мобильного попапа
     * @private
     */
    _initPopup() {
        this.popup = new ParametricFilterPopup(this.element);
        this.popup.init();
    }

    /**
     * Вешает обработчики событий
     * @private
     */
    _bindEvents() {
        this.form.addEventListener('submit', (event) => {
            event.preventDefault();
        });

        this.form.addEventListener('submit', throttle(1000, this._send.bind(this, 'show')));
        this.form.addEventListener('change', debounce(this.debTime, this._send.bind(this, 'filter')));
        this.reset.addEventListener('click', debounce(this.debTime, this._send.bind(this, 'reset')));

        this.viewButtons.forEach((viewButton) => {
            viewButton.addEventListener('click', () => {
                this._setView(viewButton);
            });
        });
    }

    _roomsEvents() {
        this.rooms.forEach((room) => {
            room.addEventListener('change', (event) => {
                if (event.currentTarget.checked) {
                    room.closest('.j-checkbox-parametric').classList.add(this.checkedClass);
                } else {
                    room.closest('.j-checkbox-parametric').classList.remove(this.checkedClass);
                }
            });
        });
    }

    _roomsUpdate() {
        this.rooms.forEach((room) => {
            if (room.checked === false) {
                room.closest('.j-checkbox-parametric').classList.remove(this.checkedClass);
            }
        });
    }

    /**
     * Отправка данных
     * @param {string} reqType - тип запроса
     * @private
     */
    _send(reqType) {
        if (this.options.change) {
            this.options.change(reqType);
        }
    }

    /**
     * Обновляет фильтры
     * @param {object} filters - данные с сервера
     * @private
     */
    _updateFilters(filters) {
        this._updateChecks(filters);
        this.selects.forEach((select) => {
            select.update(this.reqType === 'reset');
        });
        this.selectPopups.forEach((selectPopup) => {
            selectPopup.update(this.reqType === 'reset');
        });
    }

    /**
     * Обновление чекбоксов
     * @param {object} data - данные для обработки
     * @private
     */
    _updateChecks(data) {
        const checkboxItems = data.filter((item) => {
            return item.type === 'checkbox';
        });

        if (checkboxItems.length === 0) {
            return;
        }

        checkboxItems.forEach(({category, items}) => {
            const filters = Array.from(this.element.querySelectorAll(`[data-filter="${category}"]`));

            if (!filters) {
                return;
            }

            filters.forEach((filter) => {
                items.forEach((option) => {
                    const input = filter.querySelector(`input[value="${option.value}"]`);

                    if (!input) {
                        return;
                    }

                    input.disabled = option.disabled;
                    input.checked = option.checked;
                });
            });
        });

        this._roomsUpdate();
    }

    /**
     * Обновляет рейндж-слайдеры после того, как пришли новые данные с сервера
     * @param {object} ranges - данные с сервера
     * @private
     */
    _updateRanges(ranges) {
        const rangesSliders = ranges.filter((item) => {
            return item.type === 'slider';
        });

        if (rangesSliders.length === 0) {
            return;
        }

        rangesSliders.forEach(({category, value: dataForSlider}) => {
            const [from, to] = dataForSlider;
            const range = this.ranges[category];
            const data = {from,
                to};

            if (range) {
                range.update(data);
            }
        });

        this._updateSliderTitles();
    }

    _updateSliderTitles() {
        this.sliderTitles.forEach((item) => {
            item.textContent = item.textContent.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ');
        });
    }

    /**
     * Обновляет кнопку фильтра по данным с сервера
     * @param {boolean} count - количество найденых квартир
     * @private
     */
    _updateSubmit(count) {
        this.submit.classList.toggle(this.disableClass, !count);
        this.submit[count ? 'removeAttribute' : 'setAttribute']('disabled', '');
    }

    /**
     * Применяет данные с сервера
     * @param {object} data - данные с сервера
     * @param {string} reqType - тип произошедшего запроса
     */
    setFilter(data, reqType) {
        this.reqType = reqType;
        this._updateSubmit(data.premises.length);

        if (data.filters) {
            this._updateFilters(data.filters);
        }

        if (data.filters) {
            this._updateRanges(data.filters);
        }
    }

    /**
     * Поведение при показе результатов
     */
    setResult() {
        this.popup.close();
    }

    /**
     * Запускает состояние ожидания
     * @param {boolean} isSubmitLoading - надо ли лоадить кнопку поиска
     */
    runLoading(isSubmitLoading = false) {
        this.element.classList.add(this.loadingClass);

        if (isSubmitLoading) {
            this.submit.classList.add(this.loadingClass);

            this.filterElems.forEach((elem) => {
                elem.classList.add(this.elemLoadingClass);
            });
        }
    }

    /**
     * Отменяет состояние ожидания
     */
    stopLoading() {
        if (!this.element) {
            return;
        }

        this.element.classList.remove(this.loadingClass);
        this.submit.classList.remove(this.loadingClass);
        this.filterElems.forEach((elem) => {
            elem.classList.remove(this.elemLoadingClass);
        });
    }

    /**
     * Возвращает форму
     * @returns {Element} - элемент формы
     */
    getForm() {
        if (!this.element) {
            return null;
        }

        return this.form;
    }

    /**
     * Проходит по рейнджам и конвертирует значения инпутов в числа
     * @param {object} formData - данные формы, в которой нужно переписать значения
     */
    convertRanges(formData) {
        if (!this.element) {
            return;
        }
        Object.values(this.ranges).forEach((range) => {
            const inputs = range.getInputs();

            inputs.forEach((input, index) => {
                const name = input.name;
                const val = parseFloat(input.value.replace(/\s+/g, ''));
                const isChanged = index === 0 ? range.changeFrom : range.changeTo;

                if (isChanged) {
                    formData.set(name, val);
                } else {
                    formData.delete(name);
                }
            });
        });
    }

    getVisualParams() {
        const result = new URLSearchParams();

        this.visualParamInputs.forEach((item) => {
            if (item.checked) {
                result.append(item.name, item.value);
            }
        });

        return result.toString();
    }

    /**
     * Меняет вид вывода результатов
     * @param {HTMLElement} button - кнопка, на которую нажали
     * @private
     */
    _setView(button) {
        const view = button.dataset.view;

        this.viewButtons.forEach((viewButton) => {
            const method = button === viewButton ? 'add' : 'remove';

            viewButton.classList[method](this.activeClass);
        });

        this.flatsList.classList.add(this.processClass);
        this.flatsList.classList.toggle('parametric-result__flats_view_table', view === 'table');
        setTimeout(() => {
            this.flatsList.classList.remove(this.processClass);
        }, 500);
    }
}

export default ParametricFilter;
